import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Plus,
  ChevronRight,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  Layers,
} from 'lucide-react';
import {
  Card,
  CardContent,
  Button,
  Input,
  Select,
  Badge,
  Avatar,
  EmptyState,
  Pagination,
} from '../components/ui';
import { blogsApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import type { JobBatchSummary } from '../types';

// ─── Helpers ──────────────────────────────────────────────────────────────────
type SessionStatus = 'completed' | 'in_progress' | 'failed';

function getSessionStatus(s: JobBatchSummary): SessionStatus {
  if (s.failed_count === s.blog_count && s.blog_count > 0) return 'failed';
  if (s.completed_count === s.blog_count && s.blog_count > 0) return 'completed';
  return 'in_progress';
}

function rowAccent(s: JobBatchSummary) {
  const st = getSessionStatus(s);
  if (st === 'completed') return 'border-l-green-500';
  if (st === 'failed') return 'border-l-red-500';
  return 'border-l-blue-500';
}

const statusFilterOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'completed', label: 'Completed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'failed', label: 'Failed' },
];

// ─── Progress Dots ─────────────────────────────────────────────────────────────
const ProgressDots: React.FC<{ session: JobBatchSummary }> = ({ session }) => {
  const inProgress = session.blog_count - session.completed_count - session.failed_count;

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {Array.from({ length: session.completed_count }).map((_, i) => (
        <span key={`c-${i}`} className="inline-block w-2.5 h-2.5 rounded-full bg-green-500" />
      ))}
      {Array.from({ length: inProgress }).map((_, i) => (
        <span key={`p-${i}`} className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-400" />
      ))}
      {Array.from({ length: session.failed_count }).map((_, i) => (
        <span key={`f-${i}`} className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" />
      ))}
    </div>
  );
};

// ─── Status Badge ──────────────────────────────────────────────────────────────
const SessionStatusBadge: React.FC<{ session: JobBatchSummary }> = ({ session }) => {
  const status = getSessionStatus(session);
  const inProgress = session.blog_count - session.completed_count - session.failed_count;

  if (status === 'completed')
    return <Badge variant="success" icon={<CheckCircle className="h-3 w-3" />}>All completed</Badge>;
  if (status === 'failed')
    return <Badge variant="danger" icon={<AlertCircle className="h-3 w-3" />}>All failed</Badge>;
  if (session.failed_count > 0)
    return <Badge variant="warning" icon={<AlertCircle className="h-3 w-3" />}>{session.failed_count} failed</Badge>;
  return <Badge variant="info" icon={<Loader2 className="h-3 w-3 animate-spin" />}>{inProgress} in progress</Badge>;
};

// ─── Skeleton Row ──────────────────────────────────────────────────────────────
const SkeletonRow = () => (
  <div className="grid grid-cols-[1fr_2fr_1fr_1fr_40px] gap-4 px-6 py-4 items-center border-l-4 border-l-gray-200 animate-pulse">
    <div className="space-y-1.5">
      <div className="h-4 w-24 skeleton rounded" />
      <div className="h-3 w-16 skeleton rounded" />
    </div>
    <div className="flex gap-1.5">
      <div className="h-5 w-16 skeleton rounded-full" />
      <div className="h-5 w-20 skeleton rounded-full" />
      <div className="h-5 w-14 skeleton rounded-full" />
    </div>
    <div className="space-y-1.5">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((i) => <span key={i} className="inline-block w-2.5 h-2.5 rounded-full skeleton" />)}
      </div>
      <div className="h-4 w-24 skeleton rounded-full" />
    </div>
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 rounded-full skeleton" />
      <div className="h-4 w-20 skeleton rounded" />
    </div>
    <div className="h-4 w-4 skeleton rounded" />
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 10;

export const JobSessionList: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage]   = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['batches', currentPage],
    queryFn: () => blogsApi.getBatches(currentPage, PAGE_SIZE),
    refetchInterval: 15000,
    staleTime: 0,
  });

  const sessions   = data?.items ?? [];
  const totalPages = data?.total_pages ?? 1;
  const total      = data?.total ?? 0;

  // Client-side filter within current page
  const filtered = sessions.filter((s) => {
    const matchSearch =
      search === '' ||
      s.keywords.some((k) => k.toLowerCase().includes(search.toLowerCase())) ||
      s.created_by.toLowerCase().includes(search.toLowerCase());

    const st = getSessionStatus(s);
    const matchStatus =
      statusFilter === 'all' ||
      (statusFilter === 'completed' && st === 'completed') ||
      (statusFilter === 'in_progress' && st === 'in_progress') ||
      (statusFilter === 'failed' && st === 'failed');

    return matchSearch && matchStatus;
  });

  const completedSessions  = sessions.filter((s) => getSessionStatus(s) === 'completed').length;
  const inProgressSessions = sessions.filter((s) => getSessionStatus(s) === 'in_progress').length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Job Sessions</h1>
          <p className="text-gray-500 dark:text-gray-400">
            {total} sessions total · {completedSessions} completed on this page
          </p>
        </div>
        <Link to="/generate">
          <Button leftIcon={<Plus className="h-4 w-4" />}>New Generation</Button>
        </Link>
      </div>

      {/* ── Stats Strip ── */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Sessions', value: total, icon: <Layers className="h-5 w-5 text-primary-600" />, color: 'bg-primary-100 dark:bg-primary-900/30' },
          { label: 'Completed', value: completedSessions, icon: <CheckCircle className="h-5 w-5 text-green-600" />, color: 'bg-green-100 dark:bg-green-900/30' },
          { label: 'In Progress', value: inProgressSessions, icon: <Clock className="h-5 w-5 text-yellow-600" />, color: 'bg-yellow-100 dark:bg-yellow-900/30' },
        ].map((stat) => (
          <Card key={stat.label} className="relative overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-xl ${stat.color}`}>{stat.icon}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Filters ── */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by keywords or creator..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<Search className="h-5 w-5" />}
              />
            </div>
            <div className="w-full md:w-48">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={statusFilterOptions}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Error ── */}
      {error && (
        <Card className="border-red-200 dark:border-red-800">
          <CardContent className="p-4 flex items-center gap-3 text-red-600 dark:text-red-400">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm">Failed to load job sessions. Please refresh and try again.</p>
          </CardContent>
        </Card>
      )}

      {/* ── Table ── */}
      {isLoading ? (
        <Card padding="none" className="overflow-hidden">
          <div className="grid grid-cols-[1fr_2fr_1fr_1fr_40px] gap-4 px-6 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            {['Date', 'Keywords', 'Blogs', 'Created By', ''].map((h) => (
              <span key={h} className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</span>
            ))}
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {[1, 2, 3, 4, 5].map((i) => <SkeletonRow key={i} />)}
          </div>
        </Card>
      ) : filtered.length === 0 && sessions.length > 0 ? (
        <EmptyState
          title="No sessions found"
          description="Try adjusting your search or filter"
          icon={<Search className="h-8 w-8" />}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No job sessions yet"
          description="Start by generating your first batch of AI blogs"
          action={{ label: 'Generate Blogs', onClick: () => navigate('/generate') }}
        />
      ) : (
        <Card padding="none" className="overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[1fr_2fr_1fr_1fr_40px] gap-4 px-6 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            {['Date', 'Keywords', 'Blogs', 'Created By', ''].map((h) => (
              <span key={h} className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</span>
            ))}
          </div>

          {/* Rows */}
          <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {filtered.map((session, index) => (
              <motion.div
                key={session.batch_id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04 }}
                onClick={() => navigate(`/job-sessions/${session.batch_id}`)}
                className={`grid grid-cols-[1fr_2fr_1fr_1fr_40px] gap-4 px-6 py-4 items-center cursor-pointer
                  border-l-4 ${rowAccent(session)}
                  hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors group`}
              >
                {/* Date */}
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDate(session.created_at)}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {new Date(session.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>

                {/* Keywords */}
                <div className="flex flex-wrap gap-1.5 items-center">
                  {session.keywords.slice(0, 3).map((kw) => (
                    <Badge key={kw} variant="primary" size="sm">{kw}</Badge>
                  ))}
                  {session.keywords.length > 3 && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      +{session.keywords.length - 3} more
                    </span>
                  )}
                </div>

                {/* Blogs */}
                <div className="space-y-1.5">
                  <ProgressDots session={session} />
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {session.completed_count}/{session.blog_count}
                    </span>
                    <SessionStatusBadge session={session} />
                  </div>
                </div>

                {/* Created By */}
                <div className="flex items-center gap-2">
                  <Avatar name={session.created_by} size="sm" />
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                    {session.created_by}
                  </span>
                </div>

                {/* Arrow */}
                <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 dark:text-gray-600 dark:group-hover:text-gray-400 transition-colors" />
              </motion.div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Pagination ── */}
      {!isLoading && total > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          total={total}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};
